export const state = () => ({
  loading: [],
  plaidLinks: {
    links: [],
    columns: [
      {
        field: 'node.accessToken',
        label: 'accessToken',
        width: '450',
        numeric: false,
      },
      {
        field: 'node.name',
        label: 'Name',
      },
    ],
  },
  linkToken: undefined,
  oAuthURI: null,
})

export const mutations = {
  setLoading(state, payload) {
    state.loading.push(payload.key)

    // unique the array
    state.loading = [...new Set(state.loading)]

    // remove
    if (!payload.value) {
      state.loading = state.loading.filter((e) => e !== payload.key)
    }
  },
  setTempPlaidLinks(state, payload) {
    state.plaidLinks.links = payload
  },
  setLinkToken(state, linkToken) {
    state.linkToken = linkToken
  },
  setOAuthURI(state, uri) {
    state.oAuthURI = uri
  },
}

export const actions = {
  async getLinkToken(context, accessToken) {
    /*
    Action to retrieve Link token from Graph, unique to entity UUID, and set to store
      - NOTE: cannot reuse Link token, as sometimes configured with an Access Token for a specific institution
    TODO: error handling
      - if "errors" present in response
      - non 200 response
     */
    context.commit('setLoading', { key: 'getLinkToken', value: true })
    const query = `query linkToken ($accessToken: String) {
      me {
        entityUuid
        financials {
          linkToken(accessToken: $accessToken)
        }
      }
    }
    `
    const variables = {
      accessToken,
    }
    const response = await this.$axios.$post('/GRAPHQL_URL/', {
      query,
      variables,
    })
    context.commit('setLinkToken', response.data.me.financials.linkToken)
    context.commit('setLoading', { key: 'getLinkToken', value: false })
  },
  async getPlaidLinks(context, payload = {}) {
    try {
      context.commit('setLoading', { key: 'getPlaidLinks', value: true })
      const date = new Date()
      const year = payload.year ? payload.year : date.getFullYear()
      const month = payload.month ? payload.month : date.getMonth()

      console.log('*** getPlaidLinks entered..')
      const query = `
      query plaidLinks {
        me {
          financials {
            plaidLinks {
              edges {
                node {
                  name
                  accessToken
                  createdAt
                  firstFetchDate
                  latestFetchDate
                  latestFetchStatus
                  latestFetchError
                  accounts {
                    edges {
                      node {
                        accountId
                        name
                        officialName
                        balanceAvailable
                        balanceCurrent
                        transactions(year:${year}, month:${month}) {
                          edges{
                             node{
                              id
                              transactionId
                              uploadId
                              accountId
                              amount
                              adjustmentAmount
                              date
                              category {
                                description
                              }
                              adjustmentCategory {
                                description
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }`

      const response = await this.$axios.post('/GRAPHQL_URL/', {
        query,
      })

      context.commit(
        'setTempPlaidLinks',
        response?.data?.data?.me?.financials?.plaidLinks?.edges
      )
      console.log('*** getPlaidLinks success!')
    } catch (error) {
      console.log('*** getPlaidLinks ERROR:', error)
      alert(error)
    } finally {
      context.commit('setLoading', { key: 'getPlaidLinks', value: false })
    }
  },
  async unlink(context, accessToken) {
    try {
      context.commit('setLoading', { key: 'unlink', value: true })
      console.log('*** unlink entered..')

      const query = `mutation deletePlaidLink($accessToken: String!){
        me {
          deletePlaidLink(accessToken:$accessToken){
            ok
            deletedToken
          }
        }
      }`

      const variables = {
        accessToken,
      }

      await this.$axios.$post('/GRAPHQL_URL/', {
        query,
        variables,
      })
      context.dispatch('getPlaidLinks')
    } catch (error) {
      console.log(error)
      alert(`${this.$t('unlink.unlinkFailed')}`)
    } finally {
      context.commit('setLoading', { key: 'unlink', value: false })
    }
  },
  async link(context, payload) {
    try {
      context.commit('setLoading', { key: 'link', value: true })
      console.log('*** link entered..')

      const query = `mutation createPlaidLink($pli: CreatePlaidLinkInput!){
        me {
          createPlaidLink(plaidLinkInput:$pli){
            accessToken
          }
        }
      }`

      const variables = {
        pli: {
          publicToken: payload.publicToken,
          linkMetadata: payload.metadata,
        },
      }

      await this.$axios.$post('/GRAPHQL_URL/', {
        query,
        variables,
      })

      context.dispatch('getPlaidLinks')
    } catch (error) {
      console.log(error)
      alert(`${this.$t('index.linkFailed')}`)
    } finally {
      context.commit('setLoading', { key: 'link', value: false })
    }
  },
  async relink(context, accessToken) {
    try {
      context.commit('setLoading', { key: 'link', value: true })
      console.log('*** relink entered..')

      const query = `mutation relinkAccessToken($accessToken: String!){
        me{
          relinkAccessToken(accessToken: $accessToken){
            accessToken
          }
        }
      }`

      const variables = {
        accessToken,
      }

      await this.$axios.$post('/GRAPHQL_URL/', {
        query,
        variables,
      })

      context.dispatch('getPlaidLinks')
    } catch (error) {
      console.log(error)
      alert(`${this.$t('index.linkFailed')}`)
    } finally {
      context.commit('setLoading', { key: 'link', value: false })
    }
  },
}

function getObject(theObject, theKey, theValue) {
  let result = null
  if (Array.isArray(theObject)) {
    for (let i = 0; i < theObject.length; i++) {
      result = getObject(theObject[i], theKey, theValue)
      if (result) {
        return result
      }
    }
  } else {
    for (const prop in theObject) {
      console.log(prop + ': ' + theObject[prop])
      if (prop === theKey) {
        if (theObject[prop] === theValue) {
          return theObject
        }
      }
      if (theObject[prop] instanceof Object || Array.isArray(theObject[prop])) {
        result = getObject(theObject[prop], theKey, theValue)
        if (result) {
          return result
        }
      }
    }
  }
  return result
}

export const getters = {
  defaultOpenedDetailed(state) {
    return state.plaidLinks.links.map((x) => x.node)
  },
  plaidLinkByAccountId: (state) => (accountId) => {
    return getObject(state.plaidLinks.links, 'accountId', accountId)
  },
}
